import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Edit, ChevronLeft, ChevronRight } from "@mui/icons-material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import { useMaterialUIController } from "context";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import SkLoading from "components/SkLoading";

const columns = [
  { Header: "S.No", accessor: "no" },
  { Header: "User", accessor: "user" },
  { Header: "Order ID", accessor: "orderId" },
  { Header: "Amount", accessor: "amount" },
  { Header: "Payment", accessor: "payment" },
  { Header: "Refund To", accessor: "refundTo" },
  { Header: "Details", accessor: "details" },
  { Header: "Status", accessor: "status" },
  { Header: "Date", accessor: "date" },
  { Header: "Actions", accessor: "actions" },
];

const Refunds = () => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowsData, setRowsData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedRefund, setSelectedRefund] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState(""); // "REJECT" or "COMPLETE"
  const [remark, setRemark] = useState("");
  const [transactionId, setTransactionId] = useState("");

  // Fetch refunds
  const fetchRefunds = async (pageNum = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `https://api.homelyx.framekarts.com/api/admin/refunds?page=${pageNum}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.data?.success) {
        setRefunds(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
        setCurrentPage(res.data.page || pageNum);
      }
    } catch (err) {
      
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefunds(1);
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1) {
      fetchRefunds(newPage);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "APPROVED":
        return "success";
      case "REJECTED":
        return "error";
      case "COMPLETED":
        return "info";
      default:
        return "default";
    }
  };

  const handleStatusChange = (item, action) => {
    setSelectedRefund(item);
    if (action === "APPROVE") {
      handleApprove(item);
    } else if (action === "COMPLETE") {
      setModalType("COMPLETE");
      setOpenModal(true);
    } else if (action === "REJECT") {
      setModalType("REJECT");
      setOpenModal(true);
    }
  };

  const handleApprove = async (item) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `https://api.homelyx.framekarts.com/api/admin/refunds/${item._id}`,
        { status: "APPROVED" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRefunds((prev) =>
        prev.map((r) => (r._id === item._id ? { ...r, status: "APPROVED" } : r))
      );
    } catch (err) {
      
    }
  };

  const handleRejectSubmit = async () => {
    if (!remark.trim()) return alert("Please enter remark");
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `https://api.homelyx.framekarts.com/api/admin/refunds/${selectedRefund._id}`,
        { status: "REJECTED", adminRemark: remark.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRefunds((prev) =>
        prev.map((r) => (r._id === selectedRefund._id ? { ...r, status: "REJECTED" } : r))
      );
      closeModal();
    } catch (err) {
      
      alert(err.response?.data?.message || "Something went wrong");
    }
  };

  const handleCompleteSubmit = async () => {
    if (!transactionId) return alert("Please enter transaction ID");
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `https://api.homelyx.framekarts.com/api/admin/refunds/${selectedRefund._id}`,
        { status: "COMPLETED", refundTransactionId: transactionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRefunds((prev) =>
        prev.map((r) => (r._id === selectedRefund._id ? { ...r, status: "COMPLETED" } : r))
      );
      closeModal();
    } catch (err) {
      
    }
  };

  const closeModal = () => {
    setOpenModal(false);
    setRemark("");
    setTransactionId("");
  };

  useEffect(() => {
    const tempRows = refunds.map((item, index) => ({
      no: <MDTypography sx={{ fontSize: 12 }}>{(currentPage - 1) * 10 + index + 1}</MDTypography>,
      user: (
        <MDBox>
          <MDTypography fontSize={12.5}>{item.user?.fullName || "N/A"}</MDTypography>
          <MDTypography variant="caption">{item.user?.phoneNumber || "N/A"}</MDTypography>
        </MDBox>
      ),
      orderId: <MDTypography fontSize={12}>{item.orderId || "-"}</MDTypography>,
      amount: <MDTypography fontSize={12}>₹ {item.netAmount || 0}</MDTypography>,
      payment: <MDTypography fontSize={12}>{item.paymentMethod || "N/A"}</MDTypography>,
      refundTo: <MDTypography fontSize={12}>{item.upi ? "UPI" : "BANK"}</MDTypography>,
      details: (
        <MDBox
          sx={{
            width: "200px",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {item.upi ? (
            <>
              <MDTypography variant="caption" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.upi.upiId}
              </MDTypography>
              <MDTypography variant="caption" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.upi.upiHolderName}
              </MDTypography>
            </>
          ) : (
            <>
              <MDTypography variant="caption" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.bankDetails?.accountHolderName || "-"}
              </MDTypography>
              <MDTypography variant="caption" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.bankDetails?.bankName || "-"}
              </MDTypography>
            </>
          )}
        </MDBox>
      ),
      status: (
        <Chip
          label={item.status}
          color={getStatusColor(item.status)}
          size="small"
          sx={{
            fontSize: "11px",
            height: "24px",
            minWidth: "80px",
            fontWeight: 500,
          }}
        />
      ),
      date: (
        <MDTypography fontSize={10}>{new Date(item.createdAt).toLocaleDateString()}</MDTypography>
      ),
      actions: (
        <MDBox display="flex" gap={1}>
          {item.status === "PENDING" && (
            <>
              <MDButton
                size="small"
                color="success"
                onClick={() => handleStatusChange(item, "APPROVE")}
              >
                Approve
              </MDButton>
              <MDButton
                size="small"
                color="error"
                onClick={() => handleStatusChange(item, "REJECT")}
              >
                Reject
              </MDButton>
            </>
          )}
          {item.status === "APPROVED" && (
            <MDButton
              size="small"
              color="info"
              onClick={() => handleStatusChange(item, "COMPLETE")}
            >
              Complete
            </MDButton>
          )}
        </MDBox>
      ),
    }));
    setRowsData(tempRows);
  }, [refunds, currentPage]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <MDBox py={3}>
        <Card>
          <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg">
            <MDTypography variant="h6" color="white">
              Refund Requests
            </MDTypography>
          </MDBox>

          <MDBox py={3} px={2}>
            {loading ? (
              <SkLoading />
            ) : refunds.length === 0 ? (
              <MDBox display="flex" justifyContent="center">
                <MDTypography variant="h5">No Refund Found</MDTypography>
              </MDBox>
            ) : (
              <>
                <DataTable
                  table={{ columns, rows: rowsData }}
                  isSorted={false}
                  entriesPerPage={false}
                  canSearch={false}
                  showTotalEntries={false}
                  isPagination={false}
                  noEndBorder
                />
                <MDBox display="flex" justifyContent="center" alignItems="center" mt={3} gap={4}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    startIcon={<ChevronLeft />}
                  >
                    Previous
                  </MDButton>

                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={refunds.length < 10}
                    endIcon={<ChevronRight />}
                  >
                    Next
                  </MDButton>
                </MDBox>
              </>
            )}
          </MDBox>
        </Card>
      </MDBox>

      <Dialog open={openModal} onClose={closeModal}>
        <DialogTitle sx={{ color: "#000", fontWeight: 600 }}>
          {modalType === "REJECT" ? "Reject Refund" : "Complete Refund"}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#fff" }}>
          {modalType === "REJECT" ? (
            <MDInput
              label=""
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-input": { color: "#000", padding: "10px 12px" },
                "& .MuiInputLabel-root": { color: "#000", fontSize: "0.875rem" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#ccc" },
                  "&:hover fieldset": { borderColor: "#888" },
                  "&.Mui-focused fieldset": { borderColor: "#1976d2" },
                },
              }}
            />
          ) : (
            <MDInput
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              fullWidth
              sx={{
                "& .MuiInputBase-input": { color: "#000" },
                "& .MuiInputLabel-root": { color: "#000" },
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={closeModal}>Cancel</MDButton>
          <MDButton onClick={modalType === "REJECT" ? handleRejectSubmit : handleCompleteSubmit}>
            Submit
          </MDButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
};

export default Refunds;
